import { z } from 'zod';
import { DIAS_SEMANA, MAX_DIAS_DISPONIBILIDAD } from '../catalogos/agenda.js';
/**
 * Configuracion de la agenda del consultorio.
 *
 * Es un dato de la organizacion, como la marca: dos consultorios en la misma
 * instancia atienden en horarios distintos y ninguno se lee de una constante ni
 * de una variable de entorno.
 */
/** Hora del dia en formato de 24 horas. Se guarda como texto, no como fecha:
 * «se atiende de 9 a 14» no es un instante, es una regla que se repite. */
export const esquemaHoraDelDia = z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use el formato HH:MM en 24 horas');
/** Minutos transcurridos desde la medianoche. Comparar «09:30» con «14:00»
 * como texto funciona por casualidad del formato; en minutos, por construccion. */
export function minutosDeHora(hora) {
    const [horas, minutos] = hora.split(':').map(Number);
    return (horas ?? 0) * 60 + (minutos ?? 0);
}
export function horaDeMinutos(minutos) {
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return `${String(horas).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}
/**
 * Una franja de atencion. Un consultorio parte el dia en dos —mañana y tarde—
 * mas veces de las que lo deja corrido, asi que cada dia lleva una lista.
 */
export const esquemaFranja = z
    .object({
    inicio: esquemaHoraDelDia,
    fin: esquemaHoraDelDia,
})
    .refine((franja) => minutosDeHora(franja.fin) > minutosDeHora(franja.inicio), {
    message: 'La franja termina antes de empezar',
    path: ['fin'],
});
/** Franjas de un dia: ordenadas y sin encimarse. Dos franjas que se solapan
 * ofrecerian el mismo hueco dos veces. */
const esquemaFranjasDelDia = z
    .array(esquemaFranja)
    .max(4)
    .default([])
    .superRefine((franjas, ctx) => {
    const ordenadas = [...franjas].sort((a, b) => minutosDeHora(a.inicio) - minutosDeHora(b.inicio));
    for (let i = 1; i < ordenadas.length; i += 1) {
        const previa = ordenadas[i - 1];
        const actual = ordenadas[i];
        if (!previa || !actual)
            continue;
        if (minutosDeHora(actual.inicio) < minutosDeHora(previa.fin)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Las franjas ${previa.inicio}-${previa.fin} y ${actual.inicio}-${actual.fin} se enciman`,
            });
            return;
        }
    }
});
const formaHorarios = Object.fromEntries(DIAS_SEMANA.map((dia) => [dia, esquemaFranjasDelDia]));
export const esquemaHorarios = z.object(formaHorarios);
export const esquemaGuardarConfiguracionAgenda = z.object({
    /** Cuanto dura una consulta. Es el paso con el que se reparten los huecos. */
    duracionCitaMinutos: z.coerce.number().int().min(5).max(240).default(30),
    horarios: esquemaHorarios.default(() => horariosVacios()),
    /**
     * El portal publico es voluntario y nace apagado.
     *
     * Es la unica superficie del producto que escribe sin sesion: encenderla
     * tiene que ser una decision del consultorio, no lo que pasa por no tocar
     * nada.
     */
    aceptaSolicitudesPublicas: z.boolean().default(false),
    /** Cuantos dias hacia adelante puede reservar el portal publico. */
    diasReservablesPortal: z.coerce.number().int().min(1).max(MAX_DIAS_DISPONIBILIDAD).default(30),
    /** Con cuanta anticipacion minima. Sin esto, alguien pide cita para dentro
     * de diez minutos y nadie del consultorio lo ve a tiempo. */
    anticipacionMinimaHoras: z.coerce.number().int().min(0).max(168).default(4),
    /** Texto que el consultorio muestra en su portal. Sale de la organizacion,
     * no del codigo. */
    avisoPortal: z.string().trim().max(500).default(''),
});
/**
 * Semana sin atencion: todos los dias vacios. Es el punto de partida de un
 * consultorio recien registrado, que todavia no dijo cuando atiende.
 *
 * Se escribe dia por dia en lugar de derivarlo del catalogo con una conversion
 * de tipo: asi, si algun dia se agrega un dia a `DIAS_SEMANA`, el compilador
 * falla aqui en vez de dejar un horario incompleto en ejecucion.
 */
export function horariosVacios() {
    return {
        lunes: [],
        martes: [],
        miercoles: [],
        jueves: [],
        viernes: [],
        sabado: [],
        domingo: [],
    };
}
/**
 * Huecos de una franja, del tamaño de una consulta.
 *
 * Vive en los contratos y no en el backend porque los dos lados lo necesitan:
 * el servidor para comprobar que un hueco pedido existe de verdad, y la
 * interfaz para dibujar la rejilla. Dos implementaciones de esto acabarian
 * discrepando en el borde, y el borde es justo donde se agenda mal.
 *
 * El ultimo hueco tiene que caber entero: una consulta de 30 minutos no empieza
 * a las 13:50 si se cierra a las 14:00.
 */
export function huecosDeFranja(franja, duracionMinutos) {
    if (duracionMinutos <= 0)
        return [];
    const inicio = minutosDeHora(franja.inicio);
    const fin = minutosDeHora(franja.fin);
    const huecos = [];
    for (let momento = inicio; momento + duracionMinutos <= fin; momento += duracionMinutos) {
        huecos.push(horaDeMinutos(momento));
    }
    return huecos;
}
/** Huecos de un dia completo, en orden. */
export function huecosDelDia(franjas, duracionMinutos) {
    return [...franjas]
        .sort((a, b) => minutosDeHora(a.inicio) - minutosDeHora(b.inicio))
        .flatMap((franja) => huecosDeFranja(franja, duracionMinutos));
}
//# sourceMappingURL=agenda.js.map